/**
 * Players. SPEC.md §9, §8.5, §31, §47.
 *
 * The table itself, and the list of who is at it. Both live behind an icon
 * rather than on the score screen, because seating is something a table sorts
 * out once when everyone sits down, and the screen a phone is propped up on all
 * night is the keypad. §7.1, §18.2
 *
 * Two views of one fact, on purpose. The ring answers "who is sitting where",
 * which only a picture can; the list answers "what is everyone on", which only a
 * column of numbers can — and the list is also the reachable version of the drag
 * for anyone who cannot do a long press. §9.3, §19
 */

import { describeScore, entryOutcomeLabel, formatHandLabel } from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Sheet } from '@/components/ui/Sheet';
import { SignedScore } from '@/components/ui/SignedScore';
import { OfflineFor } from '@/components/ui/OfflineFor';
import {
  useActiveRound,
  useDrinkingMode,
  useHandEntryOf,
  useHandProgress,
  useIsDealer,
  useMe,
  useMyPlayerId,
  usePlayersBySeat,
  useTotalFor,
} from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';
import { TableRing } from './TableRing';

function PlayerRow({
  playerId,
  name,
  avatarKey,
  offline,
  lastSeenAt,
}: {
  playerId: string;
  name: string;
  avatarKey: string;
  offline: boolean;
  lastSeenAt: string;
}) {
  const total = useTotalFor(playerId);
  const myPlayerId = useMyPlayerId();
  const entry = useHandEntryOf(playerId);
  const drinkingMode = useDrinkingMode();
  const { winnerPlayerId } = useHandProgress();
  const isDealer = useIsDealer(playerId);

  const isMe = playerId === myPlayerId;
  const isWinner = playerId === winnerPlayerId;

  return (
    <li className="flex items-center gap-3 border-b border-line py-2 last:border-b-0">
      <span
        aria-hidden="true"
        className={[
          'grid size-10 shrink-0 place-items-center rounded-full border bg-raised',
          isMe ? 'border-accent' : 'border-line',
          offline ? 'opacity-60 grayscale' : '',
          // The same halo the ring gives them, on the same avatar. §9.5
          isDealer ? 'dealer-glow' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Avatar avatarKey={avatarKey} className="size-[86%]" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-semibold text-ink">{name}</span>
          {isMe ? (
            <span className="shrink-0 text-[0.625rem] font-bold tracking-[0.12em] text-accent uppercase">
              You
            </span>
          ) : null}
          {/* The one place the deal is said in words rather than shown as a
              glow, so the marker is never colour alone. §19 */}
          {isDealer ? (
            <span className="shrink-0 text-[0.625rem] font-bold tracking-[0.12em] text-dealer uppercase">
              Dealer
            </span>
          ) : null}
          {isWinner ? (
            <span aria-hidden="true" className="shrink-0 text-xs">
              👑
            </span>
          ) : null}
        </span>

        {/* What this hand is waiting on, per player. §3.1 */}
        <span className="truncate text-xs text-ink-faint">
          {offline ? (
            <OfflineFor since={lastSeenAt} />
          ) : isWinner ? (
            'Winning this hand'
          ) : entry ? (
            // "to everyone" describes the shape by what it does to the other
            // players, which at a drinking table is nothing at all. §4.6
            `Reported ${entryOutcomeLabel({
              shape: entry.shape,
              amount: entry.amount,
              drinkingMode,
            })}`
          ) : (
            'Yet to report'
          )}
        </span>
      </span>

      <SignedScore value={total} silent className="text-lg font-bold" />
      <span className="sr-only">
        {name}, {describeScore(total)}
      </span>
    </li>
  );
}

export function PlayersDrawer() {
  const open = useUiStore((state) => state.playersOpen);
  const close = useUiStore((state) => state.closePlayers);
  const players = usePlayersBySeat();
  const round = useActiveRound();
  const me = useMe();
  const openAvatarPicker = useUiStore((state) => state.openAvatarPicker);
  const { reportedCount, silentCount } = useHandProgress();

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Players"
      description={
        round
          ? `${formatHandLabel(round.number)} · ${String(reportedCount)} reported, ${String(silentCount)} yet to`
          : `${String(players.length)} at the table`
      }
    >
      <div className="flex flex-col gap-3 pb-2">
        {/*
          The ring gets a fixed slice of the sheet rather than a share of it: the
          seat layout is resolved against this box, so a box that grew with the
          player count would move everybody's avatar every time somebody sat
          down. §9.2
        */}
        {/* shrink-0 because this is a scrolling sheet: without it the ring is the
            one thing in here that can give, so the table is squeezed flatter by
            every row added to the list below it. §9.2 */}
        <div className="ring-scope relative h-[clamp(17rem,50dvh,25rem)] w-full shrink-0 overflow-hidden rounded-card border border-line bg-canvas">
          <TableRing />
        </div>

        <p className="text-center text-xs text-ink-faint">
          Press and hold an avatar to drag it round the table, so the ring matches
          where everyone is really sitting.
        </p>

        {/*
          §46 — changing your own character, and it belongs to every player
          rather than to the host. It lives here because this is the one screen
          that is already about who is who, and it is the only per-player setting
          past the join form: an avatar is what tells two Mikes apart, so getting
          it wrong on the way in should not cost somebody the night. §34.3, §49
        */}
        <button
          type="button"
          /* One modal at a time: the picker replaces this sheet rather than
             stacking on it, exactly as the host menu's pickers do. §47 */
          onClick={() => {
            close();
            openAvatarPicker();
          }}
          className="flex min-h-tap w-full items-center gap-3 rounded-card border border-line bg-surface px-3 py-2 text-left transition-transform duration-150 ease-out-soft active:scale-[0.99]"
        >
          {me ? <Avatar avatarKey={me.avatarKey} className="size-8 shrink-0" /> : null}
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-semibold text-ink">Change your character</span>
            <span className="text-xs text-ink-muted">
              Anything nobody else at the table is wearing.
            </span>
          </span>
        </button>

        <ul aria-label="Everyone at the table" className="flex list-none flex-col p-0">
          {players.map((player) => (
            <PlayerRow
              key={player.id}
              playerId={player.id}
              name={player.name}
              avatarKey={player.avatarKey}
              offline={player.presence === 'offline'}
              lastSeenAt={player.lastSeenAt}
            />
          ))}
        </ul>
      </div>
    </Sheet>
  );
}
